'use client';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Link } from '@/i18n/routing';
import { BlogCardClient } from './BlogCardClient';
import type { BlogPost } from './BlogList';

interface RelatedArticlesProps {
  slugs: BlogPost[];
}

export default function RelatedArticles({ slugs }: RelatedArticlesProps) {
  const t = useTranslations('blog');
  const tSlug = useTranslations('blog.slug');
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const maxIndex = Math.max(0, slugs.length - 3);
  const filteredPosts = slugs;

  const scroll = (direction: 'prev' | 'next') => {
    let newIndex = currentIndex;
    if (direction === 'prev' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (direction === 'next' && currentIndex < maxIndex) {
      newIndex = currentIndex + 1;
    }
    setCurrentIndex(newIndex);
  };

  return (
    <div id="related-articles">
      <h2 className="mt-[48px] mb-[24px] w-[800px] font-semibold text-[1.75rem] text-black leading-[140%] md:mb-[7px] md:w-full md:text-base md:leading-[140%]">
        {tSlug('relatedArticles')}
      </h2>
      <div className="relative w-[800px] md:hidden md:w-full">
        <div className="w-full overflow-hidden">
          <div
            ref={containerRef}
            className="flex gap-6 transition-all duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * (250 + 24)}px)` }}
          >
            {slugs.map((slug) => (
              <div key={slug.slug} className="w-[250px] flex-shrink-0">
                <BlogCardClient post={slug} />
              </div>
            ))}
          </div>
        </div>

        {currentIndex > 0 && (
          <button
            type="button"
            onClick={() => scroll('prev')}
            className="-left-5 -translate-y-1/2 absolute top-1/2 flex cursor-pointer items-center justify-center rounded-full border border-black/20 bg-white p-2 transition hover:border-black/40"
            aria-label="Previous articles"
          >
            <svg
              width={20}
              height={20}
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="rotate-180"
            >
              <path
                d="M7.5 15.8333L12.5 10L7.5 4.16666"
                stroke="black"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {currentIndex < maxIndex && (
          <button
            type="button"
            onClick={() => scroll('next')}
            className="-right-5 -translate-y-1/2 absolute top-1/2 flex cursor-pointer items-center justify-center rounded-full border border-black/20 bg-white p-2 transition hover:border-black/40"
            aria-label="Next articles"
          >
            <svg width={20} height={20} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M7.5 15.8333L12.5 10L7.5 4.16666"
                stroke="black"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>
      <div className="hidden w-[800px] md:block md:w-full">
        <div className="grid grid-cols-2 gap-4">
          {filteredPosts.map((post) => (
            <BlogCardClient key={post.slug} post={post} />
          ))}
        </div>
      </div>
      <div className="mt-[32px] flex w-[800px] justify-center md:mt-[20px] md:w-full">
        <Link href="/blog" prefetch={false}>
          <Button variant="primary" size="lg" className="text-base">
            {t('viewAll')}
          </Button>
        </Link>
      </div>
    </div>
  );
}
